import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
// import { useTranslation } from 'react-i18next';
import { Rocket, Target } from 'lucide-react';
import { usePremium } from '../../context/PremiumContext';
import { AdMob, BannerAdPosition, BannerAdSize } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

const DisplayAd = ({ className = "", format = "auto" }) => {
    // const { t } = useTranslation();
    const location = useLocation();
    const { isPremium } = usePremium();
    const [adAllowed, setAdAllowed] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const adRef = useRef(null);
    const pushedRef = useRef(false);
    const isNative = Capacitor.isNativePlatform();

    // Don't render ads for premium users or on competition pages
    if (isPremium || location.pathname.startsWith('/competitions')) return null;

    useEffect(() => {
        const checkConsent = () => {
            const consent = localStorage.getItem('gymtracker_cookie_consent');
            if (consent === 'all') return true;
            try {
                const parsed = JSON.parse(consent);
                return parsed && parsed.marketing === true;
            } catch {
                return false;
            }
        };

        const initAds = async () => {
            if (checkConsent()) {
                setAdAllowed(true);

                if (isNative) {
                    try {
                        await AdMob.initialize();
                    } catch (e) {
                        console.error("AdMob init error", e);
                    }
                } else if (!window.adsbygoogle_loaded) {
                    const script = document.createElement('script');
                    script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1024349425942607";
                    script.async = true;
                    script.crossOrigin = "anonymous";
                    script.onerror = () => setIsBlocked(true);
                    document.head.appendChild(script);
                    window.adsbygoogle_loaded = true;
                }
            } else {
                setAdAllowed(false);
            }
        };

        initAds();
        window.addEventListener('cookieConsentChanged', initAds);
        return () => window.removeEventListener('cookieConsentChanged', initAds);
    }, []);

    useEffect(() => {
        if (!adAllowed || isBlocked) return;

        if (isNative) {
            const showBanner = async () => {
                const options = {
                    adId: Capacitor.getPlatform() === 'android'
                        ? 'ca-app-pub-1024349425942607/9566340073' // Production Ad Unit (Banner)
                        : 'ca-app-pub-3940256099942544/2934735716', // Test ID for iOS
                    adSize: BannerAdSize.ADAPTIVE_BANNER,
                    position: BannerAdPosition.BOTTOM_CENTER,
                    margin: 0,
                    isTesting: false
                };
                try {
                    await AdMob.showBanner(options);
                    pushedRef.current = true;
                } catch (e) {
                    console.error("AdMob Banner error", e);
                    setIsBlocked(true);
                }
            };
            showBanner();
            return () => {
                AdMob.removeBanner();
            };
        } else {
            const observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && !pushedRef.current) {
                    try {
                        if (window.adsbygoogle) {
                            (window.adsbygoogle = window.adsbygoogle || []).push({});
                            pushedRef.current = true;

                            // Check if ad actually loaded after a delay
                            setTimeout(() => {
                                if (adRef.current && adRef.current.offsetHeight < 10) {
                                    setIsBlocked(true);
                                }
                            }, 2000);
                        }
                    } catch (e) {
                        if (!window.location.hostname.includes('localhost')) {
                            console.error("AdSense error", e);
                            setIsBlocked(true);
                        }
                    }
                    observer.disconnect();
                }
            }, { rootMargin: '200px' });

            if (adRef.current) {
                observer.observe(adRef.current);
            }

            return () => observer.disconnect();
        }
    }, [adAllowed, isBlocked, isNative]);

    const isLocalhost = window.location.hostname.includes('localhost');

    // On native platforms, the banner is an overlay managed by the plugin,
    // so we return null to avoid showing the web placeholder in the scroll.
    if (isNative && !isBlocked) return null;

    // On web localhost, always show the preview for development purposes
    if (!adAllowed && !isLocalhost && !isNative) return null;
    const isVertical = format === 'vertical';
    const minHeight = isVertical ? '600px' : '90px';
    const minWidth = isVertical ? '120px' : '250px';

    // "House Ad" / Fallback Content
    const HouseAd = () => (
        <div className={`w-full ${isVertical ? 'h-[600px] flex-col' : 'min-h-[120px] md:flex-row'} bg-neutral-900/40 border border-neutral-800 rounded-2xl flex items-center justify-between p-6 gap-6 relative overflow-hidden group animate-in fade-in duration-500`}>
            <div className="absolute top-0 left-0 w-1 h-full bg-red-600"></div>
            <div className="flex items-center gap-4 relative z-10">
                <div className="p-3 bg-red-900/20 rounded-xl border border-red-900/30">
                    {format === 'vertical' ? <Target className="text-red-500" size={24} /> : <Rocket className="text-red-500" size={24} />}
                </div>
                <div>
                    <h4 className="font-black uppercase italic tracking-wider text-white text-sm">
                        {isVertical ? "Top Trainer" : "Lleva tu entreno al siguiente nivel"}
                    </h4>
                    <p className="text-xs text-neutral-500 mt-1 max-w-[200px]">
                        {isVertical ? "Sigue a los mejores de la comunidad." : "Únete a la comunidad y comparte tus progresos en el Feed social."}
                    </p>
                </div>
            </div>
            <a
                href={isVertical ? "/ranking" : "/social/feed"}
                className="relative z-10 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-white text-[10px] font-bold uppercase tracking-widest rounded-lg border border-neutral-700 transition-all active:scale-95 whitespace-nowrap"
            >
                {isVertical ? "Ver Ranking" : "Ir al Feed Social"}
            </a>
            {/* Aesthetic background element */}
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-red-900/5 blur-3xl rounded-full group-hover:bg-red-900/10 transition-colors"></div>
        </div>
    );

    return (
        <div
            ref={adRef}
            className={`w-full flex flex-col items-center justify-center my-8 ${className}`}
            style={{ minHeight: minHeight }}
        >
            {isBlocked ? (
                <HouseAd />
            ) : isLocalhost ? (
                <div className={`w-full ${isVertical ? 'max-w-[160px] h-[600px]' : 'max-w-[728px] h-[90px]'} bg-neutral-900/50 border-2 border-dashed border-neutral-700 rounded-xl flex items-center justify-center animate-pulse`}>
                    <span className="text-[10px] font-black uppercase tracking-widest text-neutral-500 italic text-center p-4">
                        [ {isVertical ? 'Vertical' : 'Horizontal'} Ad Preview ]
                    </span>
                </div>
            ) : (
                <ins
                    className="adsbygoogle"
                    style={{
                        display: 'block',
                        minWidth: minWidth,
                        minHeight: minHeight,
                        width: '100%'
                    }}
                    data-ad-client="ca-pub-1024349425942607"
                    data-ad-slot="9048731331"
                    data-ad-format={format}
                    data-full-width-responsive={format === 'auto' ? "true" : "false"}
                />
            )}
        </div>
    );
};

export default DisplayAd;
